const resolveWorkDateTime = (workDt) => {
  if (!workDt) return null;

  const time = new Date(workDt).getTime();
  return Number.isNaN(time) ? null : time;
};

const resolveSortNo = (detail, fallbackIndex) => {
  const sortNo = Number(detail?.sort_no);
  return Number.isFinite(sortNo) && sortNo > 0 ? sortNo : fallbackIndex + 1;
};

const resolveBuildingSortValue = (detail) => {
  const rawBuildingId = detail?.m_building?.id ?? detail?.m_building_id;
  const buildingId = Number(rawBuildingId);
  if (Number.isFinite(buildingId) && buildingId > 0) {
    return { type: 'id', value: buildingId };
  }

  const rawBuildingName = detail?.m_building?.building_nm ?? detail?.building_nm;
  const buildingName = typeof rawBuildingName === 'string' ? rawBuildingName.trim() : '';
  if (buildingName) {
    return { type: 'name', value: buildingName };
  }

  return null;
};

const resolveBuildingGroupKey = (detail) => {
  const building = resolveBuildingSortValue(detail);
  if (!building) return null;

  return `${building.type}:${String(building.value)}`;
};

const resolveBillingGroupKey = (detail, index) => {
  if (detail?.work_type !== 2) {
    const buildingKey = resolveBuildingGroupKey(detail);
    if (buildingKey) return buildingKey;
  }

  return `detail:${index}`;
};

const compareNullableWorkDateTime = (aTime, bTime) => {
  if (aTime === null && bTime !== null) return 1;
  if (aTime !== null && bTime === null) return -1;
  if (aTime !== null && bTime !== null && aTime !== bTime) return aTime - bTime;
  return 0;
};

const compareBuildingSortValues = (aBuilding, bBuilding) => {
  if (aBuilding === null && bBuilding !== null) return 1;
  if (aBuilding !== null && bBuilding === null) return -1;
  if (aBuilding === null && bBuilding === null) return 0;

  if (aBuilding.type === 'id' && bBuilding.type === 'id') {
    return aBuilding.value - bBuilding.value;
  }

  if (aBuilding.type === 'name' && bBuilding.type === 'name') {
    return aBuilding.value.localeCompare(bBuilding.value, 'ja');
  }

  return aBuilding.type === 'id' ? -1 : 1;
};

const compareSortNo = (aDetail, bDetail, aIndex, bIndex) => {
  const aSortNo = resolveSortNo(aDetail, aIndex);
  const bSortNo = resolveSortNo(bDetail, bIndex);
  return aSortNo - bSortNo;
};

export const stripMonthlyBillingLabel = (value) => {
  if (typeof value !== 'string') return value || '';

  return value.replace(/^\s*\d{1,2}月度\s*/, '');
};

export const sortMonthlyBillingDetailsByWorkDate = (details = []) => {
  const detailsWithIndex = details.map((detail, index) => ({ detail, index }));
  const groupMetaByKey = new Map();

  detailsWithIndex.forEach(({ detail, index }) => {
    const groupKey = resolveBillingGroupKey(detail, index);
    const sortNo = resolveSortNo(detail, index);

    const workDateTime = resolveWorkDateTime(detail?.work_dt);
    const existing = groupMetaByKey.get(groupKey);
    if (!existing) {
      groupMetaByKey.set(groupKey, {
        earliestWorkDateTime: workDateTime,
        firstSortNo: sortNo,
        firstIndex: index,
        building: resolveBuildingSortValue(detail)
      });
      return;
    }

    const compareResult = compareNullableWorkDateTime(workDateTime, existing.earliestWorkDateTime);
    if (compareResult < 0) {
      existing.earliestWorkDateTime = workDateTime;
    }
    if (sortNo < existing.firstSortNo) {
      existing.firstSortNo = sortNo;
    }
    if (index < existing.firstIndex) {
      existing.firstIndex = index;
    }
  });

  return detailsWithIndex
    .sort((a, b) => {
      const aGroupKey = resolveBillingGroupKey(a.detail, a.index);
      const bGroupKey = resolveBillingGroupKey(b.detail, b.index);
      const aGroupMeta = groupMetaByKey.get(aGroupKey);
      const bGroupMeta = groupMetaByKey.get(bGroupKey);

      if (aGroupKey !== bGroupKey) {
        const groupWorkDateCompare = compareNullableWorkDateTime(
          aGroupMeta?.earliestWorkDateTime ?? null,
          bGroupMeta?.earliestWorkDateTime ?? null
        );
        if (groupWorkDateCompare !== 0) return groupWorkDateCompare;

        const groupSortNoCompare = (aGroupMeta?.firstSortNo ?? a.index + 1) - (bGroupMeta?.firstSortNo ?? b.index + 1);
        if (groupSortNoCompare !== 0) return groupSortNoCompare;

        const buildingCompare = compareBuildingSortValues(aGroupMeta?.building ?? null, bGroupMeta?.building ?? null);
        if (buildingCompare !== 0) return buildingCompare;

        return (aGroupMeta?.firstIndex ?? a.index) - (bGroupMeta?.firstIndex ?? b.index);
      }

      const aTime = resolveWorkDateTime(a.detail?.work_dt);
      const bTime = resolveWorkDateTime(b.detail?.work_dt);

      const workDateCompare = compareNullableWorkDateTime(aTime, bTime);
      if (workDateCompare !== 0) return workDateCompare;

      const sortNoCompare = compareSortNo(a.detail, b.detail, a.index, b.index);
      if (sortNoCompare !== 0) return sortNoCompare;

      return a.index - b.index;
    })
    .map(({ detail }) => detail);
};

export const sortAndNormalizeMonthlyBillingDetails = (details = []) => {
  return sortMonthlyBillingDetailsByWorkDate(details).map((detail, index) => {
    detail.sort_no = index + 1;
    return detail;
  });
};

export const buildConsecutiveBuildingGroups = (details = []) => {
  const grouped = {};

  let index = 0;
  while (index < details.length) {
    const detail = details[index];
    const buildingId = detail?.m_building?.id || detail?.m_building_id;
    const cancellationState = Boolean(detail?.cancel_annual_work);

    if (!buildingId || detail?.work_type === 2) {
      index += 1;
      continue;
    }

    let count = 1;
    while (index + count < details.length) {
      const nextDetail = details[index + count];
      const nextBuildingId = nextDetail?.m_building?.id || nextDetail?.m_building_id;
      const nextCancellationState = Boolean(nextDetail?.cancel_annual_work);

      if (
        !nextBuildingId ||
        nextDetail?.work_type === 2 ||
        nextBuildingId !== buildingId ||
        nextCancellationState !== cancellationState
      ) {
        break;
      }

      count += 1;
    }

    grouped[index] = { count, buildingId };
    index += count;
  }

  return grouped;
};
